import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  History, 
  Receipt, 
  Settings, 
  LogOut,
  Menu,
  X,
  Timer,
  Gamepad2,
  Trophy,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success('Déconnecté');
    navigate('/login');
  };

  const navItems = [
    { path: '/app/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'staff', 'cashier'] },
    { path: '/app/vendre', icon: ShoppingCart, label: 'Ventes & Prestations', roles: ['admin', 'staff', 'cashier'] },
    { path: '/app/sessions', icon: Timer, label: 'Sessions', roles: ['admin', 'staff', 'cashier'] },
    { path: '/app/historique', icon: History, label: 'Encaissements', roles: ['admin', 'staff', 'cashier'] },
    { path: '/app/depenses', icon: Receipt, label: 'Dépenses', roles: ['admin', 'staff', 'cashier'] },
    { path: '/app/depots', icon: DollarSign, label: 'Dépôts', roles: ['admin', 'staff', 'cashier'] },
    { path: '/app/admin', icon: Settings, label: 'Admin', roles: ['admin'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-[#0B132B] via-[#1C2541] to-[#0B132B]">
      {/* Top Navigation Bar - ULTRA PREMIUM */}
      <header className="shrink-0 z-50 backdrop-blur-xl bg-gradient-to-r from-[#0B132B]/98 via-[#1C2541]/98 to-[#0B132B]/98 border-b-2 border-[#C6A15B]/30 shadow-2xl xl:shadow-[0_4px_20px_rgba(198,161,91,0.3)]">
        <div className="container mx-auto px-4 py-3 lg:py-4">
          <div className="flex items-center justify-between">
            {/* Logo PREMIUM - Plus gros et mis en valeur */}
            <div className="flex items-center gap-3 lg:gap-4">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700] to-[#C6A15B] rounded-xl lg:rounded-2xl blur-md lg:blur-lg opacity-75 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative w-12 h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-[#C6A15B] via-[#FFD700] to-[#C6A15B] rounded-xl lg:rounded-2xl flex items-center justify-center shadow-2xl transform group-hover:scale-105 transition-transform">
                  <Gamepad2 className="w-7 h-7 lg:w-9 lg:h-9 text-[#0B132B]" strokeWidth={3} />
                </div>
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700] tracking-wider">
                  PlayCity
                </h1>
                <p className="text-xs lg:text-sm text-[#C6A15B]/80 font-semibold">{user?.displayName}</p>
              </div>
            </div>

            {/* Main Navigation - Desktop ONLY */}
            <nav className="hidden xl:flex items-center gap-2">
              {filteredNavItems.filter(item => !['Admin'].includes(item.label)).map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`group relative flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${
                      isActive
                        ? 'bg-gradient-to-r from-[#C6A15B] to-[#B89149] text-[#0B132B] shadow-lg shadow-[#C6A15B]/50 scale-105'
                        : 'text-[#EAEAEA] hover:bg-[#1C2541] hover:scale-105 border border-transparent hover:border-[#C6A15B]/30'
                    }`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={2.5} />
                    <span className="whitespace-nowrap">{item.label}</span>
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-[#C6A15B] to-[#B89149] rounded-xl blur-lg opacity-30 -z-10"></div>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {/* Admin Button - Desktop */}
              {user?.role === 'admin' && (
                <Link
                  to="/app/admin"
                  className="hidden lg:flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm bg-[#1C2541] text-[#C6A15B] hover:bg-[#C6A15B] hover:text-[#0B132B] border border-[#C6A15B]/30 transition-all"
                >
                  <Settings className="w-4 h-4" />
                  <span>Admin</span>
                </Link>
              )}

              {/* Logout Button - Desktop */}
              <Button
                onClick={handleLogout}
                className="hidden lg:flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl font-bold"
              >
                <LogOut className="w-4 h-4" />
                <span>Déconnexion</span>
              </Button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="xl:hidden p-2 text-[#EAEAEA] hover:bg-[#1C2541] rounded-lg transition"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 xl:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)}></div>
          <div className="absolute right-0 top-0 h-full w-80 max-w-full bg-gradient-to-br from-[#1C2541] to-[#0B132B] shadow-2xl border-l-2 border-[#C6A15B]/30 overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#C6A15B]">
                  Menu
                </h2>
                <button onClick={() => setSidebarOpen(false)} className="p-2 text-[#EAEAEA] hover:bg-[#0B132B] rounded-lg">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <nav className="space-y-3">
                {filteredNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl font-bold transition-all ${
                        isActive
                          ? 'bg-gradient-to-r from-[#C6A15B] to-[#B89149] text-[#0B132B] shadow-lg'
                          : 'text-[#EAEAEA] hover:bg-[#0B132B]'
                      }`}
                    >
                      <Icon className="w-5 h-5" strokeWidth={2.5} />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </nav>

              <div className="mt-8 pt-8 border-t border-[#C6A15B]/20 space-y-3">
                <Button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-bold"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Déconnexion</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav
        className="xl:hidden fixed bottom-0 left-0 right-0 backdrop-blur-xl bg-[#0B132B]/95 border-t-2 border-[#C6A15B]/30 z-40 overflow-x-auto"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex items-center gap-1 px-2 py-2 min-w-max">
          {filteredNavItems.filter(item => !['Admin'].includes(item.label)).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            // Shorter labels for very narrow screens
            const shortLabel = {
              'Ventes & Prestations': 'Ventes',
              'Encaissements': 'Encaiss.',
              'Dashboard': 'Dashboard',
              'Sessions': 'Sessions',
              'Dépenses': 'Dépenses',
              'Dépôts': 'Dépôts',
            }[item.label] || item.label;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 px-2 py-2 rounded-lg transition-all whitespace-nowrap min-w-[64px] ${
                  isActive
                    ? 'bg-gradient-to-r from-[#C6A15B] to-[#B89149] text-[#0B132B] shadow-lg'
                    : 'text-[#EAEAEA]'
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={2.5} />
                <span className="text-[10px] font-semibold">{shortLabel}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Main Content - SCROLLABLE CONTAINER */}
      <main
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden xl:pb-8"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)' }}
        data-testid="main-scroll-container"
      >
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
